/**
 * Token estimation utilities for context injection.
 *
 * Provides conservative token count estimation for budget-aware data injection.
 */
import { encode } from '@toon-format/toon';

/**
 * Estimate token count from string length.
 * Uses conservative 1 token per 4 characters (English text average).
 * TOON encoding is more efficient than JSON, so this intentionally overestimates.
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Iteratively reduce records to fit within token limit.
 * Uses TOON encoding and reduces by 30% each iteration until under limit.
 */
export function reduceToTokenLimit<T extends Record<string, unknown>>(records: T[], maxTokens: number): T[] {
  if (records.length === 0) {
    return records;
  }

  let current = records;
  let encoded = encode(current);

  while (estimateTokens(encoded) > maxTokens && current.length > 1) {
    // Reduce by 30% each iteration
    current = current.slice(0, Math.floor(current.length * 0.7));
    encoded = encode(current);
  }

  return current;
}
